import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import "./cashFlows.css";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function minusOneDay(value) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

function money(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return amount < 0 ? `(₦${abs})` : `₦${abs}`;
}

function sum(list) {
  return list.reduce((total, value) => total + Number(value || 0), 0);
}

function normalise(text) {
  return String(text || "")
    .trim()
    .toLowerCase();
}

function isCashAccount(accountName) {
  const name = normalise(accountName);
  return (
    name.includes("cash") ||
    name.includes("bank") ||
    name.includes("wallet") ||
    name.includes("petty cash")
  );
}

function isDepreciationAccount(accountName) {
  return normalise(accountName).includes("depreciation");
}

function isReceivableAccount(accountName) {
  const name = normalise(accountName);
  return name.includes("receivable") || name.includes("debtors");
}

function isPayableAccount(accountName) {
  const name = normalise(accountName);
  return name.includes("payable") || name.includes("creditors");
}

function isInventoryAccount(accountName) {
  return normalise(accountName).includes("inventory");
}

function isTaxPayableAccount(accountName) {
  const name = normalise(accountName);
  return name.includes("tax") && name.includes("payable");
}

function isInvestingAccount(accountName) {
  const name = normalise(accountName);
  return (
    name.includes("fixed asset") ||
    name.includes("property") ||
    name.includes("plant") ||
    name.includes("equipment") ||
    name.includes("motor vehicle") ||
    name.includes("furniture") ||
    name.includes("investment")
  );
}

function isFinancingAccount(accountName) {
  const name = normalise(accountName);
  return (
    name.includes("loan") ||
    name.includes("borrow") ||
    name.includes("note payable") ||
    name.includes("capital") ||
    name.includes("equity") ||
    name.includes("dividend") ||
    name.includes("drawings")
  );
}

function balanceFromRows(rows, matcher, typeHint) {
  return (rows || []).reduce((total, row) => {
    if (!matcher(row.account_name)) return total;

    const debit = Number(row.total_debit || 0);
    const credit = Number(row.total_credit || 0);
    const type = normalise(row.account_type || typeHint);

    const isDebitNormal = ["asset", "expense", "cost of sales"].includes(type);
    const balance = isDebitNormal ? debit - credit : credit - debit;

    return total + balance;
  }, 0);
}

const CashFlows = () => {
  const { companyId } = useCompany();

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState(todayISO());

  const [opsOpen, setOpsOpen] = useState(true);
  const [invOpen, setInvOpen] = useState(true);
  const [finOpen, setFinOpen] = useState(true);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [data, setData] = useState({
    revenue: 0,
    costOfSales: 0,
    expenses: 0,
    netProfit: 0,
    depreciation: 0,

    arCashEffect: 0,
    inventoryCashEffect: 0,
    apCashEffect: 0,
    taxPayableCashEffect: 0,

    netCashOps: 0,
    investingCashFlow: 0,
    financingCashFlow: 0,
    netCashFlow: 0,

    openingCash: 0,
    closingCash: 0,
    actualCashMovement: 0,
    checkDifference: 0,
  });

  useEffect(() => {
    const year = new Date().getFullYear();
    setStartDate(`${year}-01-01`);
    setEndDate(todayISO());
  }, []);

  const fetchData = useCallback(async () => {
    if (!companyId || !startDate || !endDate) return;

    setLoading(true);
    setErr("");

    try {
      const startMinusOne = minusOneDay(startDate);

      const [periodRes, openingRes, closingRes] = await Promise.all([
        supabase.rpc("get_cashflow_account_sums", {
          p_company_id: companyId,
          p_start_date: startDate,
          p_end_date: endDate,
        }),
        startMinusOne
          ? supabase.rpc("get_cashflow_bs_sums_upto", {
              p_company_id: companyId,
              p_end_date: startMinusOne,
            })
          : Promise.resolve({ data: [], error: null }),
        supabase.rpc("get_cashflow_bs_sums_upto", {
          p_company_id: companyId,
          p_end_date: endDate,
        }),
      ]);

      if (periodRes.error) throw periodRes.error;
      if (openingRes.error) throw openingRes.error;
      if (closingRes.error) throw closingRes.error;

      const periodRows = periodRes.data || [];
      const openingRows = openingRes.data || [];
      const closingRows = closingRes.data || [];

      const revenue = sum(
        periodRows
          .filter((row) => row.account_type === "Revenue")
          .map(
            (row) =>
              Number(row.total_credit || 0) - Number(row.total_debit || 0)
          )
      );

      const costOfSales = sum(
        periodRows
          .filter((row) => row.account_type === "Cost of Sales")
          .map(
            (row) =>
              Number(row.total_debit || 0) - Number(row.total_credit || 0)
          )
      );

      const expenses = sum(
        periodRows
          .filter((row) => row.account_type === "Expense")
          .map(
            (row) =>
              Number(row.total_debit || 0) - Number(row.total_credit || 0)
          )
      );

      const netProfit = revenue - costOfSales - expenses;

      const depreciation = sum(
        periodRows
          .filter((row) => isDepreciationAccount(row.account_name))
          .map((row) =>
            Math.abs(
              Number(row.total_debit || 0) - Number(row.total_credit || 0)
            )
          )
      );

      const arOpening = balanceFromRows(
        openingRows,
        isReceivableAccount,
        "Asset"
      );
      const arClosing = balanceFromRows(
        closingRows,
        isReceivableAccount,
        "Asset"
      );
      const arCashEffect = -(arClosing - arOpening);

      const inventoryOpening = balanceFromRows(
        openingRows,
        isInventoryAccount,
        "Asset"
      );
      const inventoryClosing = balanceFromRows(
        closingRows,
        isInventoryAccount,
        "Asset"
      );
      const inventoryCashEffect = -(inventoryClosing - inventoryOpening);

      const apOpening = balanceFromRows(
        openingRows,
        isPayableAccount,
        "Liability"
      );
      const apClosing = balanceFromRows(
        closingRows,
        isPayableAccount,
        "Liability"
      );
      const apCashEffect = apClosing - apOpening;

      const taxOpening = balanceFromRows(
        openingRows,
        isTaxPayableAccount,
        "Liability"
      );
      const taxClosing = balanceFromRows(
        closingRows,
        isTaxPayableAccount,
        "Liability"
      );
      const taxPayableCashEffect = taxClosing - taxOpening;

      const netCashOps =
        netProfit +
        depreciation +
        arCashEffect +
        inventoryCashEffect +
        apCashEffect +
        taxPayableCashEffect;

      /**
       * Conservative classification:
       * Investing/financing sections are derived from non-cash balance-sheet
       * account movements during the period.
       *
       * For perfect IAS 7 compliance later, add a cash_flow_section column to
       * chart_of_accounts or tag source transactions directly.
       */
      const investingCashFlow = sum(
        periodRows
          .filter((row) => isInvestingAccount(row.account_name))
          .map(
            (row) =>
              Number(row.total_credit || 0) - Number(row.total_debit || 0)
          )
      );

      const financingCashFlow = sum(
        periodRows
          .filter((row) => isFinancingAccount(row.account_name))
          .map(
            (row) =>
              Number(row.total_credit || 0) - Number(row.total_debit || 0)
          )
      );

      const netCashFlow = netCashOps + investingCashFlow + financingCashFlow;

      const openingCash = balanceFromRows(openingRows, isCashAccount, "Asset");
      const closingCash = balanceFromRows(closingRows, isCashAccount, "Asset");
      const actualCashMovement = closingCash - openingCash;
      const checkDifference = netCashFlow - actualCashMovement;

      setData({
        revenue,
        costOfSales,
        expenses,
        netProfit,
        depreciation,

        arCashEffect,
        inventoryCashEffect,
        apCashEffect,
        taxPayableCashEffect,

        netCashOps,
        investingCashFlow,
        financingCashFlow,
        netCashFlow,

        openingCash,
        closingCash,
        actualCashMovement,
        checkDifference,
      });
    } catch (error) {
      console.error("Cashflows fetch error:", error);
      setErr(error?.message || "Failed to compute cash flows.");
    } finally {
      setLoading(false);
    }
  }, [companyId, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isReconciled = Math.abs(Number(data.checkDifference || 0)) < 0.01;

  return (
    <div className="cf-container">
      <div className="cf-header">
        <div>
          <h2 className="cf-title">Statement of Cash Flows</h2>
          <p className="cf-sub">
            For the period from <strong>{startDate || "…"}</strong> to{" "}
            <strong>{endDate || "…"}</strong>
          </p>
        </div>
      </div>

      <div className="cf-filter-card">
        <div className="cf-row">
          <div className="input-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>

          <div className="input-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>

          <div className="input-group">
            <label>&nbsp;</label>
            <button className="cf-primary" onClick={fetchData}>
              Run Cash Flow
            </button>
          </div>
        </div>

        <p className="cf-help">
          Cash flow is a period report. It explains how cash and bank balances
          moved between the start and end dates.
        </p>

        {err && <p className="cf-error">{err}</p>}
      </div>

      <div className={`cf-check-card ${isReconciled ? "ok" : "warn"}`}>
        <strong>{isReconciled ? "Reconciled" : "Needs Review"}</strong>
        <p>
          Net cash flow should equal the actual movement in cash and bank
          accounts. Difference: <strong>{money(data.checkDifference)}</strong>
        </p>
      </div>

      <div className="cf-summary-grid">
        <div className="cf-summary-card">
          <span>Opening Cash</span>
          <strong>{money(data.openingCash)}</strong>
        </div>

        <div className="cf-summary-card">
          <span>Net Cash Flow</span>
          <strong>{money(data.netCashFlow)}</strong>
        </div>

        <div className="cf-summary-card">
          <span>Closing Cash</span>
          <strong>{money(data.closingCash)}</strong>
        </div>

        <div className="cf-summary-card">
          <span>Actual Cash Movement</span>
          <strong>{money(data.actualCashMovement)}</strong>
        </div>
      </div>

      <div className="cf-card">
        {loading ? (
          <div className="cf-loading">Loading cash flow statement…</div>
        ) : (
          <>
            <section className="cf-section">
              <button
                className="cf-accordion"
                onClick={() => setOpsOpen((value) => !value)}
              >
                <span>Cash Flows from Operating Activities</span>
                <span className="chev">{opsOpen ? "▾" : "▸"}</span>
              </button>

              {opsOpen && (
                <div className="cf-body">
                  <div className="cf-line">
                    <span>Net Profit / Loss</span>
                    <span className="num">{money(data.netProfit)}</span>
                  </div>

                  <div className="cf-subheader">Non-cash adjustments</div>

                  <div className="cf-line">
                    <span>Add back: Depreciation</span>
                    <span className="num">{money(data.depreciation)}</span>
                  </div>

                  <div className="cf-subheader">Working capital movements</div>

                  <div className="cf-line">
                    <span>Accounts Receivable Movement</span>
                    <span className="num">{money(data.arCashEffect)}</span>
                  </div>

                  <div className="cf-line">
                    <span>Inventory Movement</span>
                    <span className="num">
                      {money(data.inventoryCashEffect)}
                    </span>
                  </div>

                  <div className="cf-line">
                    <span>Accounts Payable Movement</span>
                    <span className="num">{money(data.apCashEffect)}</span>
                  </div>

                  <div className="cf-line">
                    <span>Taxes Payable Movement</span>
                    <span className="num">
                      {money(data.taxPayableCashEffect)}
                    </span>
                  </div>

                  <div className="cf-line total">
                    <strong>Net Cash from Operating Activities</strong>
                    <strong className="num">{money(data.netCashOps)}</strong>
                  </div>
                </div>
              )}
            </section>

            <section className="cf-section">
              <button
                className="cf-accordion"
                onClick={() => setInvOpen((value) => !value)}
              >
                <span>Cash Flows from Investing Activities</span>
                <span className="chev">{invOpen ? "▾" : "▸"}</span>
              </button>

              {invOpen && (
                <div className="cf-body">
                  <div className="cf-line total">
                    <span>Net Investing Cash Flow</span>
                    <span className="num">{money(data.investingCashFlow)}</span>
                  </div>
                </div>
              )}
            </section>

            <section className="cf-section">
              <button
                className="cf-accordion"
                onClick={() => setFinOpen((value) => !value)}
              >
                <span>Cash Flows from Financing Activities</span>
                <span className="chev">{finOpen ? "▾" : "▸"}</span>
              </button>

              {finOpen && (
                <div className="cf-body">
                  <div className="cf-line total">
                    <span>Net Financing Cash Flow</span>
                    <span className="num">{money(data.financingCashFlow)}</span>
                  </div>
                </div>
              )}
            </section>

            <section className="cf-section">
              <div className="cf-line total grand">
                <strong>Net Increase / Decrease in Cash</strong>
                <strong className="num">{money(data.netCashFlow)}</strong>
              </div>

              <div className="cf-line">
                <span>Cash and Cash Equivalents at Beginning of Period</span>
                <span className="num">{money(data.openingCash)}</span>
              </div>

              <div className="cf-line grand">
                <strong>Cash and Cash Equivalents at End of Period</strong>
                <strong className="num">{money(data.closingCash)}</strong>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default CashFlows;
